import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _typeof from "@babel/runtime/helpers/typeof";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import handlebars from '@sparta.global.vendors/handlebars';
import { json } from '@sparta.global.helpers/json-helper/dist/es5.js';

/**
 * Helper to render all the modules within a given layout area.
 * @file
 */

/**
 * Decodes html entities.
 * @param {string} input The input to decode
 * @returns {string} The decoded html entity.
 */
function htmlDecode(input) {
  return typeof input !== 'string' ? input : input.replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
}

/**
 * Extracts all text from the given markup into the provided property on "object".
 * @param {string} markup The markup to extract from.
 * @param {RegExp} regexp A regular expression to match strings to remove.
 * @param {object} object The object containing a property to store the extracted text.
 * @param {string} prop The property on "object" to srie the extract text.
 * @param {string} join A separator to join extracted contents.
 * @returns {object} The originally passed in object.
 */
function extract(markup, regexp, object, prop) {
  var join = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : '\n';
  var config = object;
  var inlined = [];
  var extracted = markup.replace(regexp, function ($0, $1) {
    var trimmed = $1.trim();
    if (trimmed) inlined.push(trimmed);
    return '';
  });
  config[prop] = "".concat(config[prop] || '').concat(join).concat(inlined.join(join));
  return extracted;
}

/**
 * Generates the [data-module-parameters] data-attribute.
 * @param {object} context The rendering context.
 * @returns {string} The stringified module parameters.
 */
function generateModuleParameterDataAttribute(context) {
  var moduleParameters = {};
  // console.log('generateModuleParameterDataAttribute', JSON.stringify(context.module, null, '   '))
  // Get actual parameters that have been set and also default parameter values
  var contextModuleParams = context.module.moduleParams;
  if (!Array.isArray(contextModuleParams)) {
    contextModuleParams = Object.values(contextModuleParams);
  }
  contextModuleParams.forEach(function (param) {
    moduleParameters[param.name] = Object.hasOwnProperty.call(context.module || {}, param.name) ? context.module[param.name] : param.default;
  });
  return "data-module-parameters='".concat(JSON.stringify(moduleParameters), "'");
}

/**
 * Attempts to parse a value as JSON. If fails, it returns the original value.
 * @param {string} value The value to attempt to parse.
 * @returns {string} The parsed value.
 */
function maybeJsonParse(value) {
  try {
    return JSON.parse(value);
  } catch (e) {
    return value;
  }
}

/**
 * Recursively walks the given input object and renders all strings with the given context.
 * @param {object} input The object to walk/render.
 * @param {object} context The handlebars rendering context.
 * @returns {object} The originally passed in object.
 */
function recursivelyRenderExpressions(input, context) {
  if (!input || _typeof(input) !== 'object') return input;
  var object = {};
  Object.keys(input).forEach(function (key) {
    var value = input[key];

    // If It's a string with {{, render it as a handlebars template.
    // Else if it's an object, call this method recursively.
    if (typeof value === 'string' && value.indexOf('{{') > -1) {
      object[key] = maybeJsonParse(htmlDecode(handlebars.compile(value)(context, {
        helpers: {
          json: json
        },
        partials: {}
      })));
    } else if (Array.isArray(value)) {
      object[key] = value.map(function (val) {
        return recursivelyRenderExpressions(val, context);
      });
    } else {
      object[key] = recursivelyRenderExpressions(input[key], context);
    }
  });
  return object;
}

/**
 * Renders a module.
 * @param {string} env The page's environment.
 * @param {string} language The page's language.
 * @param {object} siteConfig The site's JSON configuration.
 * @param {object} pageConfig The page's JSON configuration.
 * @param {object} platformModule The module's JSON configuration.
 * @param {object} rules Page rules.
 * @returns {string} The rendered module html.
 */
function renderModule(env, language, siteConfig, pageConfig, platformModule, rules, partials, helpers) {
  var config = platformModule.config;
  var dataSources = platformModule.dataSources || {};
  var moduleName = config.moduleName;
  var moduleVersion = config.version;

  // This is the handlebars rendering context
  // used to render the module's html with.
  var context = {
    env: env,
    language: language,
    moduleName: moduleName,
    global: siteConfig.global[language],
    page: pageConfig.parameters,
    site: siteConfig.params,
    module: _objectSpread({}, config),
    children: platformModule.children,
    footnotes: pageConfig.footnotes,
    partials: partials,
    helpers: helpers
  };
  var wrapModule = siteConfig.nonWrappedModules;

  // Populate context data source(s) on the context
  // based on the current language value.
  Object.keys(dataSources).forEach(function (name) {
    context[name] = dataSources[name][language] ? dataSources[name][language] : dataSources[name];
  });

  // Walk module parameter JSON object and replace all handlebars
  // expressions with content from the rendering context.
  Object.assign(context.module, recursivelyRenderExpressions(platformModule.params || {}, context));

  // add module data to the module's context if present
  if (config.data && _typeof(config.data.options) === 'object') {
    context.module.data = recursivelyRenderExpressions(config.data, context);
  }

  // The tag type for the module's enclosing element and it's id="" attribute.
  var tag = config.tag || 'div';
  var id = platformModule.id || config.id || '';

  // Makes the ID of the container available in the template being rendered
  context.module.containerID = id;

  // The modules scss namespace and its "versioned" namespace
  var nameSpace = config.nameSpace || moduleName;
  var modNameSpaceVersion = "".concat(nameSpace, "-class-v-").concat(moduleVersion).replace(/\./g, '-');
  var dataInit = '';
  var spaModuleInit = '';
  var dataOptions = '';
  if (config.data && typeof config.data.init === 'string') {
    dataInit = "data-init=\"".concat(config.data.init, "\"");
    spaModuleInit = 'spa-module-init';
  }

  // Insert data options from processed context
  if (context.module.data && _typeof(context.module.data.options) === 'object') {
    dataOptions = "data-options='".concat(JSON.stringify(context.module.data.options), "'");
  }

  // Determine the module's require loop load type
  var group = !platformModule.primaryloadType && pageConfig.hasSecondary ? 'secondary' : 'primary';
  var dataSpartaLoad = "data-sparta-load=\"".concat(group, "\"");
  var dataRef = "data-module-ref=".concat(platformModule.name);
  (context.module.load || {}).group = group;

  // Process and create module header html.
  // This will resolve any template bar syntax used in module config data.
  var wrapper = handlebars.compile("\n\n<".concat(tag, " ").concat(id ? "id=\"".concat(id, "\"") : '', " data-component=\"module\" ") + "data-module=\"".concat(moduleName, "\" data-version=\"").concat(moduleVersion, "\" ") + "".concat(dataSpartaLoad, " ").concat(dataRef, " ").concat(dataOptions, " ").concat(dataInit, " ").concat(generateModuleParameterDataAttribute(context), " ") + "class=\"".concat(modNameSpaceVersion, " ").concat(nameSpace, " ").concat(spaModuleInit, "\">"))(context, {
    helpers: {
      json: json
    },
    partials: {}
  });

  // Render the module's inner "context" html using context
  var html = '';
  if (!rules[moduleName] || rules[moduleName].indexOf('addModule') === -1) {
    // Render module using pre-compiled template, partials and helpers from module config
    // const { template, partials, helpers, name, type } = platformModule.config;
    // html = template(context, { partials, helpers });

    // Render module using config render function unless it is a vue component
    if (platformModule.config.type !== 'vue') {
      html = platformModule.config.render(context);
    }

    // html = handlebars.compile(`{{>${moduleName}}}`)(context);  // CBN replaced by module config render method

    // Extract module in-line CSS and JS to move to page head tag and foot respectively
    if (siteConfig.params.inlineCode && !platformModule.isOnDemandModule) {
      html = extract(html, /<script>([\s\S]*?)<\/script>/g, pageConfig, 'inlineJS', ';');
      html = extract(html, /<style>([\s\S]*?)<\/style>/g, pageConfig, 'inlineCSS', '\n');
    }
  }
  var output = "".concat(html, "\n</").concat(tag, ">\n\n");
  return wrapModule !== true ? "".concat(wrapper, "\n").concat(output) : output;
}

/**
 * Render a module's HTML
 * @param {object} siteConfig Site JSON configuration
 * @param {object} pageConfig Page JSON Configuration
 * @param {string} language The page's language.
 * @param {string} env The current build environment.
 * @param {string} assetPath The local html path to assets (images, etc.).
 * @param {object} moduleArray An array of module objects.
 * @returns {string} The rendered module html markup.
 */
function moduleContainer(siteConfig, pageConfig, language, env, assetPath, moduleArray) {
  return moduleArray.map(function (mod) {
    return helpers.composeModule(siteConfig, pageConfig, language, env, assetPath, mod);
  }).join('');
}

/**
 * Render a module's HTML
 * @param {object} siteConfig Site JSON configuration
 * @param {object} pageConfig Page JSON Configuration
 * @param {string} language The page's language.
 * @param {string} env The current build environment.
 * @param {string} assetPath The local html path to assets (images, etc.).
 * @param {object} platformModule An object with a lot of data from the platform.
 * @returns {string} The rendered module html markup.
 */
function composeModule(siteConfig, pageConfig, language, env, assetPath, platformModule, partials, helpers) {
  if (!siteConfig.global) throw new Error("The site's global configuration is undefined.");
  var rules = {};
  var childModules = platformModule.childModules;

  // Extract rules by name
  if (Array.isArray(platformModule.rules)) {
    platformModule.rules.forEach(function (rule) {
      rules[platformModule.config.moduleName] = rule.actions.map(function (action) {
        return action.type;
      });
    });
  }

  // Rendering child modules, if any.
  if (Array.isArray(childModules)) {
    var children = {};
    childModules.forEach(function (childModule) {
      var id = childModule.id || childModule.config.shortName || childModule.config.id || childModule.config.moduleName;
      children[id] = renderModule(env, language, siteConfig, pageConfig, childModule, rules, childModule.config.partials, childModule.config.helpers);
    });
    Object.assign(platformModule, {
      children: children
    });
  }

  // Rendering regular modules...
  return renderModule(env, language, siteConfig, pageConfig, platformModule, rules, partials, helpers);
}
export { composeModule, moduleContainer };